/**
 * §P.9 fase 2 · Previsión de caja del mes que viene.
 *
 * ## Las devoluciones vienen en negativo para que sumen
 *
 * Lo dice el DTO y es la única regla de este informe: los conceptos se suman tal
 * cual, sin interpretar signos en la pantalla. Así que aquí no hay ninguna resta —
 * pintar un menos delante de un número que ya es negativo daría «−−40 €»— y el total
 * es la suma de la columna.
 *
 * ## Es una previsión, y se dice
 *
 * Nadie debería planificar una nómina con esto sin saber que son cuotas activas
 * proyectadas, no dinero comprometido. Una cifra grande y sola en una pantalla se
 * lee como un saldo.
 */
import { api } from '@/shared/api/client';
import { ErrorDeApi, aProblema } from '@/shared/api/errores';
import { euros } from '@/shared/ui/formato';
import { EnvolturaDeInforme } from '@/club/informes/Envoltura';
import {
  etiquetaDeConcepto,
  type FilaDePrevision,
  type Informe,
} from '@/club/informes/tipos';

export function PrevisionDeCaja({ club }: { club: string }) {
  return (
    <EnvolturaDeInforme<FilaDePrevision>
      club={club}
      clave="prevision"
      ruta="cash-forecast"
      titulo="Previsión de caja"
      explicacion={
        <>
          Lo que se espera cobrar el mes que viene, por conceptos.{' '}
          <strong>Es una previsión</strong>: sale de las cuotas activas de hoy,
          no de dinero comprometido, y cambia con cada alta y cada baja.
        </>
      }
      vacio={{
        titulo: 'No hay nada que prever',
        explicacion:
          'Sin cuotas activas no hay previsión posible. Asigna tarifas a los alumnos y esto empezará a decir algo.',
      }}
      pedir={async () => {
        const { data, error } = await api.v1
          .clubs({ club })
          .reports['cash-forecast'].get({ query: {} });
        if (error) throw new ErrorDeApi(aProblema(error));
        return data as unknown as Informe<FilaDePrevision>;
      }}
    >
      {(datos) => {
        /* Se suma tal cual: las devoluciones ya vienen en negativo. */
        const total = datos.rows.reduce((s, f) => s + f.cents, 0);

        return (
          <>
            <dl className="datos">
              <div>
                <dt>Se espera cobrar</dt>
                <dd>{euros(total)}</dd>
              </div>
            </dl>

            <div className="tabla-envoltura">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>Concepto</th>
                    <th>De dónde sale</th>
                    <th className="num">Importe</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.rows.map((f, i) => (
                    <tr key={`${f.concept}-${i}`}>
                      {/* En español: el servidor los manda en `snake_case`. */}
                      <td>{etiquetaDeConcepto(f.concept)}</td>
                      <td>{f.detail}</td>
                      {/*
                        `euros` ya pinta el signo del negativo: poner otro delante
                        daría «−−40 €». Ver la cabecera.
                      */}
                      <td className="num">{euros(f.cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="tenue">
              Lo que resta —devoluciones estimadas— viene ya en negativo, así
              que el total es la suma de la columna.
            </p>
          </>
        );
      }}
    </EnvolturaDeInforme>
  );
}
